<template>
    <b-modal
        title-tag="h2"
        title="Send Email"
        :lazy="true"
        :visible="isOpen"
        no-enforce-focus
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form>
            <b-form-group
                label-cols="3"
                label="Patient(s):"
                label-for="patient"
            >
                <vue-select
                    v-model="patient"
                    label="name"
                    :placeholder="patientPlaceholder"
                    :disabled="isLoadingList"
                    :options="patientOptions"
                    :clearable="true"
                    class="show-actions"
                    multiple
                >
                    <template v-slot:open-indicator>
                        <span></span>
                    </template>
                    <template #list-footer v-if="isLoadingMore">
                        <div class="loading-dots loading-more">
                            Loading more
                        </div>
                    </template>
                </vue-select>
            </b-form-group>

            <b-form-group label-cols="3" label="To:" label-for="to">
                <recepients-email
                    v-model="recipient"
                    multiple
                    placeholder="Client or Contact Email (required)"
                    :client="clientDetails"
                    :contact="contactDetails"
                    :key="recipientComponentKey"
                    v-validate="'required'"
                    name="recipient"
                    :class="{
                        'border-danger': formError.includes('recipient')
                    }"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Subject:" label-for="subject">
                <b-form-input
                    v-model="subject"
                    placeholder="Subject (required)"
                    v-validate="'required'"
                    name="subject"
                    :class="{
                        'border-danger': formError.includes('subject')
                    }"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Message:" label-for="Message">
            </b-form-group>

            <tinymce
                :init="tinymceConfig"
                placeholder="Communication (required)"
                v-model="message"
            />
        </b-form>

        <template v-slot:modal-footer>
            <loader v-if="isSending" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>
<style type="text/css">
.loading-more {
    padding-left: 1.6em;
}
</style>
<script>
import ClientDocument from "@/services/client-documents";
import ClientCommunication from "@/services/client-communications";
import vueSelect from "vue-select";
import RecepientsEmail from "@/components/commons/recipients-email";
import Loader from "@/module/commons/loader";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce";
import COMMUNICATION from "@/constants/communications";
import ContactRelationship from "@/services/contact-relationships";
import PatientHelper from "@/mixins/patient";
import { filter, uniqBy, union, find } from "lodash";

export default {
    mixins: [PatientHelper],
    components: {
        vueSelect,
        RecepientsEmail,
        Loader,
        tinymce
    },
    props: {
        module: {
            String,
            required: true,
            validator: function (value) {
                // The value must match one of these strings
                return (
                    [
                        COMMUNICATION.MODULE_CLIENT,
                        COMMUNICATION.MODULE_CONTACT
                    ].indexOf(value) !== -1
                );
            }
        },
        contactDetails: {
            type: Object
        },
        clientDetails: {
            type: Object
        }
    },
    data() {
        return {
            communication: null, // if not null then this is resend
            patient: [],
            subject: "",
            recipient: [],
            message: "",
            isOpen: false,
            isSending: false,
            isLoadingList: true,
            isLoadingMore: false,
            patientOptions: [],
            recipientComponentKey: 0,
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                height: 400,
                toolbar: tinymceHelper.toolbar.default, // default | communication
                plugins: tinymceHelper.plugins
            },
            formError: [],
            listCurrentPage: null
        };
    },
    computed: {
        patientPlaceholder() {
            return this.isLoadingList ? "Loading..." : "Patient(s) (optional)";
        },
        isCompany() {
            return this.$store.state.contact.isCompany;
        }
    },
    watch: {
        recipient(value) {
            if (value.length > 0) {
                this.formError = this.formError.filter(
                    err => err !== "recipient"
                );
            }
        },
        subject(value) {
            if (value !== "") {
                this.formError = this.formError.filter(
                    err => err !== "subject"
                );
            }
        }
    },
    methods: {
        open(communication) {
            this.communication = communication || null;
            let hasEmail = false;
            if (this.module == COMMUNICATION.MODULE_CLIENT) {
                hasEmail =
                    this.clientDetails &&
                    this.clientDetails.email &&
                    this.clientDetails.email !== "";
                this.fetchPatientListClient(this.clientDetails.id);
            } else if (this.module == COMMUNICATION.MODULE_CONTACT) {
                this.fetchPatientListContact(this.contactDetails.id);
                hasEmail =
                    this.contactDetails &&
                    this.contactDetails.email &&
                    this.contactDetails.email !== "";
            }

            if (hasEmail) {
                this.recipientComponentKey++; // force re-render
            }

            if (this.communication) {
                this.message = this.communication.content;
            }

            this.isOpen = true;
        },
        close() {
            this.isOpen = false;
        },
        async fetchPatientListClient(clientId) {
            this.isLoadingList = true;
            this.patientOptions = await ClientDocument.getPatient(clientId);
            this.setPatient();
            this.isLoadingList = false;
        },
        setPatient() {
            if (this.communication && this.communication.patient_id) {
                const existingPatient = find(this.patientOptions, {
                    id: this.communication.patient_id
                });
                if (existingPatient) {
                    this.patient = [existingPatient];
                }
            }
        },
        async send() {
            try {
                this.formError = [];
                let hasError = false;

                if (this.recipient.length === 0) {
                    this.formError.push("recipient");
                    hasError = true;
                }

                if (this.subject === "") {
                    this.formError.push("subject");
                    hasError = true;
                }

                if (hasError) {
                    return;
                }

                this.isSending = true;

                let params = {
                    sender_id:
                        this.module == COMMUNICATION.MODULE_CLIENT
                            ? this.clientDetails.id
                            : this.contactDetails.id,
                    rtype: COMMUNICATION.RTYPE[this.module],
                    timestamp: Math.floor(new Date().getTime() / 1000),
                    module_id: COMMUNICATION.MODULE_ID[this.module],
                    subject: this.subject,
                    message: this.message,
                    patient: this.patient.map(patient => patient.id),
                    to: this.recipient.map(rec => rec.to_id),
                    is_system_generated: false
                };

                if (
                    this.module == COMMUNICATION.MODULE_CONTACT &&
                    this.isCompany
                ) {
                    params.module_id =
                        COMMUNICATION.MODULE_ID[
                            COMMUNICATION.MODULE_CONTACT_COMPANY
                        ];
                }
                let sent = true;
                if (this.communication) {
                    // Code to resend email
                    params.resent_communication = true;
                    params.communication_id = this.communication.id;
                }

                sent = await ClientCommunication.sendEmail(params);

                if (sent) {
                    this.isSending = false;
                    this.close();
                    this.$emit("onSendSuccess");
                    this._message("Email successfully sent");
                } else {
                    throw new Error("Error: Unable to send email");
                }
            } catch (e) {
                this.isSending = false;
                this._message(e.message, "danger");
            }
        },
        async fetchPatientListContact(contactId, loadMore = false) {
            this.isLoadingList = true;

            if (loadMore) {
                this.isLoadingList = false;
                this.isLoadingMore = loadMore;
            }

            if (this.isCompany) {
                await ContactRelationship.getCompanyRelationship(contactId, {
                    perPage: 50,
                    page: this.listCurrentPage ? this.listCurrentPage + 1 : null
                })
                    .then(res => {
                        this.filterContactRelationships(res, loadMore);
                        this.checkMoreList(res);
                    })
                    .finally(() => {
                        this.isLoadingList = false;
                    });
            } else {
                await ContactRelationship.getContactRelationship(contactId, {
                    perPage: 50,
                    page: this.listCurrentPage ? this.listCurrentPage + 1 : null
                })
                    .then(res => {
                        this.filterContactRelationships(res, loadMore);
                        this.checkMoreList(res);
                    })
                    .finally(() => {
                        this.isLoadingList = false;
                    });
            }
        },
        checkMoreList(res) {
            let meta = res.meta;
            this.listCurrentPage = meta.currentPage;

            // Skip checking if this modal is already closed
            if (meta && meta.currentPage < meta.lastPage && this.isOpen) {
                this.fetchPatientListContact(this.contactDetails.id, true);
            } else {
                this.isLoadingMore = false;
            }
        },
        filterContactRelationships(res, loadMore = false) {
            let self = this;
            let filteredPatients = filter(res.resources, patient => {
                patient.name = patient.name + " " + self.getStatus(patient);
                return patient.entity_type == "patient";
            });

            if (loadMore) {
                this.patientOptions = union(
                    this.patientOptions,
                    uniqBy(filteredPatients, "id")
                );
            } else {
                this.patientOptions = uniqBy(filteredPatients, "id");
            }
            this.setPatient();
        }
    }
};
</script>
